import RichText from "../RichText/RichText";
import { Accordion, AccordionDetails, AccordionSummary, Typography } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { FC, useEffect, useState } from "react";

interface Question {
  id: string;
  question: string;
  answers?: string[];
  answer?: string;
  link?: { href: string; text: string };
}

export interface IFaq {
  title?: string;
  list: Question[];
}

interface FaqComponent extends FC<IFaq> {
  noJsRequired: true;
}

export const Faq: FaqComponent = ({ list, title }) => {
  return (
    <>
      {title && (
        <Typography component={"h2"} variant="h5">
          {title}
        </Typography>
      )}
      {list.map((qa, index) => (
        <FaqAccordian key={`faq-${index}`} qa={qa} />
      ))}
    </>
  );
};

interface FaqAccordianProps {
  qa: Question;
}

const FaqAccordian: FC<FaqAccordianProps> = ({ qa }) => {
  // Auto scroll and open dialog summary based on #id or a #:~:text (text fragment)
  // https://github.com/whatwg/html/issues/4051
  // https://html.spec.whatwg.org/#interaction-with-details-and-hidden=until-found
  const [isJs, setIsJs] = useState(false);
  useEffect(() => setIsJs(true), []);
  return (
    <Accordion
      //Must be div when isJs since else Enter doesn't work
      component={isJs ? "div" : "details"}
      slotProps={{
        heading: {
          component: "summary",
          tabIndex: -1,
          sx: {
            borderRadius: 4,
            display: "block",
            ".MuiAccordionSummary-root": {
              borderRadius: 2,
            },
          },
        },
      }}
      sx={({ vars }) => ({
        overflow: "hidden",
        interpolateSize: "allow-keywords",
        borderRadius: 2,
        marginBottom: 2,
        ":first-of-type": {
          borderTopLeftRadius: `calc(2 * ${vars.shape.borderRadius})`,
          borderTopRightRadius: `calc(2 * ${vars.shape.borderRadius})`,
        },
        ":last-of-type": {
          borderBottomLeftRadius: `calc(2 * ${vars.shape.borderRadius})`,
          borderBottomRightRadius: `calc(2 * ${vars.shape.borderRadius})`,
        },
        ".MuiAccordionSummary-root:focus": {
          backgroundColor: "rgba(0,0,0,0.12)",
        },
        "&::before": {
          height: 0,
        },
        "&::details-content": {
          height: 0,
          overflow: "clip",
          opacity: 0,
          transition: "height 300ms ease-in-out, opacity 300ms ease-in-out, content-visibility 300ms allow-discrete",
        },
        "&[open]::details-content": {
          height: "auto",
          opacity: 1,
        },
        "&[open]": {
          ".MuiCollapse-root": {
            height: "auto",
            visibility: "visible",
          },
          ".MuiAccordionSummary-expandIconWrapper": {
            transform: "rotate(180deg)",
          },
        },
      })}
    >
      <AccordionSummary
        expandIcon={<ExpandMore color="primary" />}
        aria-controls={`${qa.question}-content`}
        id={qa.question}
        slotProps={{ root: { component: "div" } }}
      >
        <Typography variant="h6" component={"h3"} sx={{ fontWeight: "bold" }}>
          {qa.question}
        </Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          p: {
            margin: 0,
            paddingBottom: "0.75em",
          },
          ul: {
            paddingLeft: "1.3em",
            li: {
              listStyleType: "disc",
            },
            "ul li": {
              listStyleType: "circle",
            },
          },
        }}
      >
        {qa.answer && <RichText html={qa.answer} />}
      </AccordionDetails>
    </Accordion>
  );
};

Faq.noJsRequired = true;
